import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { NowPlaying } from '@daily/shared';
import { api, type SonosAction, type SonosState } from '@/lib/api';
import { X, Play, Pause, SkipBack, SkipForward, Speaker, Volume2, VolumeX, Info, Search } from 'lucide-react';
import { SpotifySearch } from './SpotifySearch';

interface NowPlayingExpandedProps {
  initial: NowPlaying | null;
  onClose: () => void;
}

export function NowPlayingExpanded({ initial, onClose }: NowPlayingExpandedProps) {
  const qc = useQueryClient();

  // Sonos state (groups + players + now playing) — refreshed every 5s while
  // the modal is open. Sonos is the source of truth for everything music.
  const sonos = useQuery<SonosState>({
    queryKey: ['sonos', 'state'],
    queryFn: () => api.getSonosState(),
    refetchInterval: () => (document.visibilityState === 'visible' ? 5000 : false),
    staleTime: 1000,
  });

  const [controlError, setControlError] = useState<string | null>(null);

  const control = useMutation({
    mutationFn: (vars: {
      action: SonosAction;
      groupId?: string;
      playerId?: string;
      playerIds?: string[];
      volumePercent?: number;
    }) => api.sonosControl(vars.action, vars),
    onSuccess: () => {
      setControlError(null);
      qc.invalidateQueries({ queryKey: ['sonos', 'state'] });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : 'Couldn’t control playback.';
      setControlError(msg);
    },
  });

  const [searchOpen, setSearchOpen] = useState(false);

  // User's room selection — local state so an explicit tap always sticks.
  // Priority: explicit user pick > whatever's currently playing > first group.
  // Without the user-pick taking precedence, music starting in another room
  // would yank control away from the room the user just picked.
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const playingGroup = sonos.data?.groups.find(
    (g) => g.playbackState === 'PLAYBACK_STATE_PLAYING',
  );
  const userPickedGroup = selectedGroupId
    ? sonos.data?.groups.find((g) => g.id === selectedGroupId)
    : null;
  const activeGroup = userPickedGroup ?? playingGroup ?? sonos.data?.groups[0] ?? null;
  const isPlaying = activeGroup?.playbackState === 'PLAYBACK_STATE_PLAYING';
  const track: NowPlaying | null = activeGroup?.track ?? initial ?? null;

  // Local volume draft so dragging is smooth.
  const [volumeDraft, setVolumeDraft] = useState<number | null>(null);
  const liveVolume = activeGroup?.volume ?? 0;
  const displayedVolume = volumeDraft ?? liveVolume;
  useEffect(() => {
    setVolumeDraft(null);
  }, [activeGroup?.id]);

  const onVolumeCommit = useMemo(
    () => (v: number) => {
      if (!activeGroup) return;
      control.mutate({ action: 'volume', groupId: activeGroup.id, volumePercent: v });
    },
    [control, activeGroup],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg shadow-lg overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #1F2D44 0%, #5B2A4A 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.18em] uppercase opacity-60">
              {searchOpen ? 'Find music' : `Now playing · ${activeGroup?.name ?? 'no Sonos detected'}`}
            </span>
            {!searchOpen && (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-[10px] tracking-[0.14em] uppercase transition-colors"
              >
                <Search size={11} /> Search
              </button>
            )}
          </div>

          {searchOpen && (
            <SpotifySearch
              selectedGroupName={activeGroup?.name ?? null}
              onClose={() => setSearchOpen(false)}
            />
          )}

          {/* Album art + track */}
          <div className="flex gap-4 items-start">
            {track?.artUrl ? (
              <img
                src={track.artUrl}
                alt=""
                className="w-24 h-24 rounded-md shadow-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-24 h-24 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                <Volume2 size={24} className="opacity-40" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-[20px] leading-tight">{track?.track || 'Silence'}</div>
              <div className="text-[13px] opacity-70 mt-1 truncate">{track?.artist}</div>
              {track?.album && (
                <div className="text-[11px] opacity-50 mt-0.5 truncate">{track.album}</div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {track?.playing && (
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full transition-[width] duration-1000"
                style={{
                  width: `${track.progressPct ?? 0}%`,
                  background: 'linear-gradient(90deg, #FFB088, #E94B6B)',
                }}
              />
            </div>
          )}

          {/* Volume slider — Sonos accepts volume on all rooms reliably */}
          <div className="flex items-center gap-2.5 mt-1">
            {displayedVolume === 0 ? (
              <VolumeX size={16} className="opacity-60" />
            ) : (
              <Volume2 size={16} className="opacity-60" />
            )}
            <input
              type="range"
              min={0}
              max={100}
              value={displayedVolume}
              onChange={(e) => setVolumeDraft(Number(e.target.value))}
              onMouseUp={(e) => onVolumeCommit(Number((e.target as HTMLInputElement).value))}
              onTouchEnd={(e) => onVolumeCommit(Number((e.target as HTMLInputElement).value))}
              disabled={!activeGroup}
              className="flex-1 accent-rose-300 disabled:opacity-40 cursor-pointer"
              aria-label="Volume"
            />
            <span className="font-mono text-[10px] opacity-60 w-7 text-right">
              {displayedVolume}
            </span>
          </div>

          {controlError && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-rose-200/15 border border-rose-200/25 text-[11px] leading-snug">
              <Info size={12} className="flex-shrink-0 mt-0.5 opacity-70" />
              <span className="opacity-90">{controlError}</span>
            </div>
          )}

          {/* Transport controls */}
          <div className="flex items-center justify-center gap-6 mt-2">
            <button
              type="button"
              onClick={() => activeGroup && control.mutate({ action: 'previous', groupId: activeGroup.id })}
              disabled={control.isPending || !activeGroup}
              className="p-2 rounded-full hover:bg-white/10 transition-colors disabled:opacity-40"
              aria-label="Previous"
            >
              <SkipBack size={22} fill="currentColor" />
            </button>
            <button
              type="button"
              onClick={() =>
                activeGroup &&
                control.mutate({ action: isPlaying ? 'pause' : 'play', groupId: activeGroup.id })
              }
              disabled={control.isPending || !activeGroup}
              className="p-3 rounded-full bg-white text-ink hover:scale-105 transition-transform disabled:opacity-40"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
            </button>
            <button
              type="button"
              onClick={() => activeGroup && control.mutate({ action: 'next', groupId: activeGroup.id })}
              disabled={control.isPending || !activeGroup}
              className="p-2 rounded-full hover:bg-white/10 transition-colors disabled:opacity-40"
              aria-label="Next"
            >
              <SkipForward size={22} fill="currentColor" />
            </button>
          </div>

          {/* Group / room picker */}
          <div className="mt-2">
            <div className="text-[10px] tracking-[0.18em] uppercase opacity-60 mb-2 flex items-center gap-1.5">
              <Speaker size={12} /> Rooms
            </div>
            {sonos.isLoading ? (
              <p className="text-[12px] opacity-60">Reading household…</p>
            ) : (sonos.data?.groups ?? []).length === 0 ? (
              <p className="text-[12px] opacity-60 italic">
                No Sonos groups found. Make sure speakers are powered on.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {(sonos.data?.groups ?? []).map((g) => {
                  const isPlayingHere = g.playbackState === 'PLAYBACK_STATE_PLAYING';
                  const isSelected = g.id === activeGroup?.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setSelectedGroupId(g.id)}
                      className={`flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors ${
                        isSelected
                          ? 'bg-white/20'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                      title={g.name}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Speaker size={14} className="flex-shrink-0 opacity-70" />
                        <span className="text-[13px] truncate">{g.name}</span>
                        <span className="text-[9px] opacity-40 uppercase tracking-wider flex-shrink-0">
                          {g.playerIds.length === 1 ? 'room' : `${g.playerIds.length} rooms`}
                        </span>
                      </span>
                      {isPlayingHere ? (
                        <span className="text-[9px] tracking-[0.14em] uppercase text-rose-200">
                          Playing
                        </span>
                      ) : isSelected ? (
                        <span className="text-[9px] tracking-[0.14em] uppercase opacity-60">
                          Selected
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
